// Simple client-side image compression utility
// Uses canvas resizing and quality reduction for JPEG/WEBP.
// Falls back to original file if anything fails.
export interface CompressOptions {
  maxWidth?: number; // default 1600
  maxHeight?: number; // default 1600
  quality?: number; // 0.0 - 1.0 (default 0.82)
  mimeType?: string; // 'image/webp' | 'image/jpeg'
}

export async function compressImage(file: File, opts: CompressOptions = {}): Promise<File> {
  try {
    // GIF (animation) and SVG (vector) would be flattened by a canvas re-encode.
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) return file;
    const maxWidth = opts.maxWidth ?? 1600;
    const maxHeight = opts.maxHeight ?? 1600;
    const quality = opts.quality ?? 0.82;
    let mimeType = opts.mimeType ?? (file.type === 'image/png' ? 'image/webp' : file.type);
    if (!/image\/(jpeg|png|webp)/i.test(mimeType)) mimeType = 'image/jpeg';

    const bitmap = await createImageBitmap(file).catch(async () => {
      const img = await loadImageFallback(URL.createObjectURL(file));
      return await imageToBitmap(img);
    });
    const { width, height } = bitmap;
    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);
    // iOS Safari caps canvas area (~16.7MP) and can silently produce a blank image beyond it; keep the original.
    if (targetW * targetH > 16_000_000) return file;
    const canvas = document.createElement('canvas');
    canvas.width = targetW; canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    const encode = (type: string): Promise<Blob | null> => new Promise((resolve) => canvas.toBlob(resolve, type, quality));
    let blob = await encode(mimeType);
    // Browsers without a WebP encoder silently return PNG; fall back to JPEG for opaque sources.
    if (blob && blob.type !== mimeType && file.type !== 'image/png') {
      mimeType = 'image/jpeg';
      blob = await encode(mimeType);
    }
    if (!blob || blob.type !== mimeType || blob.size >= file.size) return file; // skip if larger or unsupported
    return new File([blob], deriveCompressedName(file.name, mimeType), { type: mimeType });
  } catch {
    return file;
  }
}

function deriveCompressedName(name: string, mime: string) {
  const ext = mime.includes('webp') ? 'webp' : (mime.includes('png') ? 'png' : 'jpg');
  const base = name.replace(/\.[^.]+$/,'');
  return base + '_cmp.' + ext;
}

function loadImageFallback(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function imageToBitmap(img: HTMLImageElement): Promise<ImageBitmap> {
  return await createImageBitmap(img);
}

// Optional helper: batch compress multiple images sequentially
export async function compressImages(files: File[], opts?: CompressOptions): Promise<File[]> {
  const out: File[] = [];
  for (const f of files) {
    out.push(await compressImage(f, opts));
  }
  return out;
}
